import React, { useCallback, useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getServicioStyle } from "../../../utils/servicioColores";
import HistorialFilaDrawer from "../../../components/HistorialFilaDrawer";
import PanelFlotante from "../../../components/PanelFlotante";
import BotonVolver from "../../../components/BotonVolver";

// ─────────────────────────────────────────────
// Filtro de columna tipo Excel: lista de valores únicos con checkboxes + buscador
// ─────────────────────────────────────────────
const ColumnFilterPopover = ({ opciones, seleccionados, onChange, align = "center" }) => {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [temp, setTemp] = useState(seleccionados);
  const ref = useRef(null);

  useEffect(() => { if (abierto) setTemp(seleccionados); }, [abierto, seleccionados]);
  const cerrar = useCallback(() => setAbierto(false), []);

  const etiqueta = (o) => (o === "" ? "(vacío)" : String(o));
  const filtradas = busqueda.trim()
    ? opciones.filter((o) => etiqueta(o).toLowerCase().includes(busqueda.trim().toLowerCase()))
    : opciones;
  const toggle = (v) => setTemp((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  const activo = seleccionados.length > 0;

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block", marginLeft: 6 }}>
      <button
        onClick={(e) => { e.stopPropagation(); setAbierto((a) => !a); }}
        title="Filtrar"
        style={{
          border: "none", background: activo ? "#1e3a5f" : "rgba(255,255,255,0.25)",
          color: "#fff", borderRadius: 8, cursor: "pointer",
          fontSize: 13, padding: "4px 9px", lineHeight: 1.4, fontWeight: 800,
        }}
      >▾</button>
      {abierto && (
        <PanelFlotante anchorRef={ref} onCerrar={cerrar} style={{
          background: "#fff",
          border: "1.5px solid #e2e8f0", borderRadius: 14, boxShadow: "0 18px 40px rgba(15,23,42,0.28)",
          width: 260, padding: 14, textAlign: "left", color: "#334155", textTransform: "none", fontWeight: 400,
        }}>
          <input
            value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="🔍 Buscar…"
            style={{ width: "100%", padding: "9px 10px", borderRadius: 9, border: "1.5px solid #e2e8f0", fontSize: 14, marginBottom: 8, boxSizing: "border-box", outline: "none" }}
          />
          <div style={{ display: "flex", gap: 12, marginBottom: 8, fontSize: 12.5 }}>
            <span onClick={() => setTemp(filtradas)} style={{ color: "#3498db", cursor: "pointer", fontWeight: 700 }}>Marcar todos</span>
            <span onClick={() => setTemp([])} style={{ color: "#94a3b8", cursor: "pointer", fontWeight: 700 }}>Desmarcar</span>
          </div>
          <div style={{ maxHeight: 230, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
            {filtradas.length === 0 && <div style={{ fontSize: 13, color: "#94a3b8", padding: 4 }}>Sin valores</div>}
            {filtradas.map((o) => (
              <label key={o} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer", padding: "4px 2px" }}>
                <input type="checkbox" checked={temp.includes(o)} onChange={() => toggle(o)} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{etiqueta(o)}</span>
              </label>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={() => { onChange([]); setAbierto(false); }} style={{ flex: 1, padding: "8px 0", borderRadius: 9, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Limpiar</button>
            <button onClick={() => { onChange(temp); setAbierto(false); }} style={{ flex: 1, padding: "8px 0", borderRadius: 9, border: "none", background: "#3498db", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Aplicar</button>
          </div>
        </PanelFlotante>
      )}
    </span>
  );
};

const AdminGestionPisos = () => {
  const [pisos, setPisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importando, setImportando] = useState(false);
  const [error, setError] = useState("");
  const [drawer, setDrawer] = useState(null); // { filaId, titulo } | null

  const [pisoModal, setPisoModal] = useState(null);

  const [filtros, setFiltros] = useState({
    id: [],
    abreviatura: [],
    empresa: [],
    servicio: [],
    asistenta: [],
    supervisora: [],
    comprador: [],
    ruta: [],
    franjaHoraria: [],
    supermercado: [],
  });
  const [filtroEstado, setFiltroEstado] = useState("todos"); // "todos" | "alta" | "baja"
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPisos = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/pisos/`);
        const ordenados = res.data.sort((a, b) =>
          a.abreviatura.localeCompare(b.abreviatura)
        );
        setPisos(ordenados);
        setError("");
      } catch (err) {
        setError("❌ Error al cargar pisos.");
      } finally {
        setLoading(false);
      }
    };

    fetchPisos();
  }, []);

  // Extraer opciones únicas para los filtros desplegables (incluye "" si hay pisos sin ese dato)
  const opcionesUnicas = (campo) => {
    const vistos = new Set();
    const opciones = [];
    let hayVacios = false;
    pisos.forEach((p) => {
      const v = p[campo];
      if (v === null || v === undefined || v === "") { hayVacios = true; return; }
      if (!vistos.has(v)) { vistos.add(v); opciones.push(v); }
    });
    opciones.sort();
    if (hayVacios) opciones.push("");
    return opciones;
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Dar de baja este piso?")) return;
    try {
      await axios.delete(`/api/pisos/${id}`);
      setPisos((prev) => prev.filter((p) => p.id !== id));
    } catch {
      alert("❌ No se pudo dar de baja el piso.");
    }
  };

  const handleImportar = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,.xlsx,.xls";

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);

      try {
        setImportando(true);
        const res = await axios.post(`/api/pisos/importar`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const desc = Array.isArray(res.data?.filas_descartadas) ? res.data.filas_descartadas : [];
        let msg = `✅ Importados ${res.data?.total_importados ?? 0} pisos.`;
        msg += `\n🆕 Altas: ${res.data?.altas ?? 0} · ✏️ Modificados: ${res.data?.modificaciones ?? 0} · 🚫 Bajas: ${res.data?.bajas_detectadas ?? 0}`;
        if (desc.length) msg += `\nℹ️ Descartados: ${desc.length} (empresa≠CH/CM o plazas=0).`;
        alert(msg);
        window.location.reload();
      } catch (err) {
        const dataErr = err.response?.data;
        let msg = dataErr?.error || "❌ Error al importar.";
        if (Array.isArray(dataErr?.filas_error) && dataErr.filas_error.length) {
          msg += "\n\nErrores:\n" + dataErr.filas_error.slice(0, 10).map(e => `  Fila ${e.fila}: ${e.motivo}`).join("\n");
        }
        alert(msg);
      } finally {
        setImportando(false);
      }
    };

    input.click();
  };

  // Filtrado combinado
  const pisosFiltrados = pisos.filter((piso) => {
    if (filtroEstado === "alta" && piso.activo === 0) return false;
    if (filtroEstado === "baja" && piso.activo !== 0) return false;

    if (filtros.id.length && !filtros.id.includes(piso.id))
      return false;

    if (filtros.abreviatura.length && !filtros.abreviatura.includes(piso.abreviatura))
      return false;

    if (filtros.empresa.length && !filtros.empresa.includes(piso.empresa ?? ""))
      return false;

    if (filtros.servicio.length && !filtros.servicio.includes(piso.servicio ?? ""))
      return false;

    if (filtros.asistenta.length && !filtros.asistenta.includes(piso.nombre_asistenta ?? ""))
      return false;

    if (filtros.supervisora.length && !filtros.supervisora.includes(piso.user_supervisora ?? ""))
      return false;

    if (filtros.comprador.length && !filtros.comprador.includes(piso.user_comprador ?? ""))
      return false;

    if (filtros.ruta.length && !filtros.ruta.includes(piso.ruta ?? ""))
      return false;

    if (filtros.franjaHoraria.length && !filtros.franjaHoraria.includes(piso.franja_horaria ?? ""))
      return false;

    if (filtros.supermercado.length && !filtros.supermercado.includes(piso.supermercado ?? ""))
      return false;

    return true;
  });

  const hayFiltrosActivos = filtroEstado !== "todos" || filtros.id.length > 0 || filtros.abreviatura.length > 0 || filtros.empresa.length > 0
    || filtros.servicio.length > 0 || filtros.asistenta.length > 0 || filtros.supervisora.length > 0
    || filtros.comprador.length > 0 || filtros.ruta.length > 0 || filtros.franjaHoraria.length > 0
    || filtros.supermercado.length > 0;
  const limpiarFiltros = () => {
    setFiltros({ id: [], abreviatura: [], empresa: [], servicio: [], asistenta: [], supervisora: [], comprador: [], ruta: [], franjaHoraria: [], supermercado: [] });
    setFiltroEstado("todos");
  };

  return (
  <div style={styles.container}>
    <BotonVolver fallback="/admin" />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <h2 style={styles.title}>Gestión de Pisos</h2>

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div style={styles.estadoGroup}>
          {[
            { key: "todos", label: `Todos (${pisos.length})` },
            { key: "alta", label: `Altas (${pisos.filter((p) => !esBaja(p)).length})` },
            { key: "baja", label: `Bajas (${pisos.filter(esBaja).length})` },
          ].map((op) => (
            <button
              key={op.key}
              onClick={() => setFiltroEstado(op.key)}
              style={filtroEstado === op.key ? styles.estadoBtnActivo : styles.estadoBtn}
            >
              {op.label}
            </button>
          ))}
        </div>
        {hayFiltrosActivos && (
          <button style={styles.clearFilters} onClick={limpiarFiltros}>
            ❌ Quitar filtros
          </button>
        )}
        <button
          style={styles.historial}
          onClick={() => navigate("/admin/pisos/historial")}
          onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(94, 53, 177, 0.45)"; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 10px rgba(94, 53, 177, 0.35)"; }}
        >
          📜 Historial
        </button>
        <button
          style={styles.import}
          onClick={handleImportar}
          disabled={importando}
        >
          {importando ? "⏳ Importando..." : "📥 Importar CSV/Excel"}
        </button>
      </div>
    </div>

      {!loading && !importando && !error && (
        <div style={styles.contadorFiltro}>
          🏠 {hayFiltrosActivos ? (
            <>Mostrando <strong>{pisosFiltrados.length}</strong> de <strong>{pisos.length}</strong> pisos</>
          ) : (
            <><strong>{pisos.length}</strong> pisos en total</>
          )}
        </div>
      )}

      {loading ? (
        <p>Cargando pisos...</p>
      ) : importando ? (
        <p>Importando pisos...</p>
      ) : error ? (
        <p style={{ color: "red" }}>{error}</p>
      ) : pisosFiltrados.length === 0 ? (
        <p style={{ marginTop: 30, textAlign: "center" }}>No se encontraron pisos ({pisos.length} en total, filtros muy restrictivos).</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>ID</span><ColumnFilterPopover opciones={opcionesUnicas("id")} seleccionados={filtros.id} onChange={(v) => setFiltros((f) => ({ ...f, id: v }))} align="left" /></div>
              </th>
              <th style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>Abreviatura</span><ColumnFilterPopover opciones={opcionesUnicas("abreviatura")} seleccionados={filtros.abreviatura} onChange={(v) => setFiltros((f) => ({ ...f, abreviatura: v }))} /></div>
              </th>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>Empresa</span><ColumnFilterPopover opciones={opcionesUnicas("empresa")} seleccionados={filtros.empresa} onChange={(v) => setFiltros((f) => ({ ...f, empresa: v }))} /></div>
              </th>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>Servicio</span><ColumnFilterPopover opciones={opcionesUnicas("servicio")} seleccionados={filtros.servicio} onChange={(v) => setFiltros((f) => ({ ...f, servicio: v }))} /></div>
              </th>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>Asistenta</span><ColumnFilterPopover opciones={opcionesUnicas("nombre_asistenta")} seleccionados={filtros.asistenta} onChange={(v) => setFiltros((f) => ({ ...f, asistenta: v }))} /></div>
              </th>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>Supervisora</span><ColumnFilterPopover opciones={opcionesUnicas("user_supervisora")} seleccionados={filtros.supervisora} onChange={(v) => setFiltros((f) => ({ ...f, supervisora: v }))} /></div>
              </th>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>Comprador</span><ColumnFilterPopover opciones={opcionesUnicas("user_comprador")} seleccionados={filtros.comprador} onChange={(v) => setFiltros((f) => ({ ...f, comprador: v }))} /></div>
              </th>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>Ruta</span><ColumnFilterPopover opciones={opcionesUnicas("ruta")} seleccionados={filtros.ruta} onChange={(v) => setFiltros((f) => ({ ...f, ruta: v }))} /></div>
              </th>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>Franja Horaria</span><ColumnFilterPopover opciones={opcionesUnicas("franja_horaria")} seleccionados={filtros.franjaHoraria} onChange={(v) => setFiltros((f) => ({ ...f, franjaHoraria: v }))} /></div>
              </th>
              <th className="ocultar-en-movil" style={styles.headerCell}>
                <div style={styles.hdrFlex}><span>Supermercado</span><ColumnFilterPopover opciones={opcionesUnicas("supermercado")} seleccionados={filtros.supermercado} onChange={(v) => setFiltros((f) => ({ ...f, supermercado: v }))} /></div>
              </th>
              <th style={{ ...styles.headerCell, minWidth: 172 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pisosFiltrados.map((piso) => { const baja = esBaja(piso); return (
              <tr
                key={piso.id}
                style={{ ...styles.row, cursor: "pointer", ...(baja ? styles.rowBaja : {}) }}
                title={baja ? `Piso dado de baja${piso.fecha_baja ? ` el ${fechaBajaTexto(piso.fecha_baja)}` : ""}` : undefined}
                onClick={() => setPisoModal(piso)}
              >
                <td className="ocultar-en-movil" style={{ ...styles.cell, ...(baja ? styles.cellBajaPrimera : {}) }}>{piso.id}</td>
                <td style={styles.cell}>
                  <strong style={baja ? { textDecoration: "line-through", color: "#64748b" } : undefined}>{piso.abreviatura}</strong>
                  {baja && (
                    <div style={{ marginTop: 4 }}>
                      <span style={styles.badgeBaja}>🚫 DE BAJA{piso.fecha_baja ? ` · ${fechaBajaTexto(piso.fecha_baja)}` : ""}</span>
                    </div>
                  )}
                </td>
                <td className="ocultar-en-movil" style={styles.cell}>{piso.empresa || ""}</td>
                <td className="ocultar-en-movil" style={styles.cell}>{piso.servicio || ""}</td>
                <td className="ocultar-en-movil" style={styles.cell}>{piso.nombre_asistenta || ""}</td>
                <td className="ocultar-en-movil" style={styles.cell}>{piso.user_supervisora}</td>
                <td className="ocultar-en-movil" style={styles.cell}>{piso.user_comprador}</td>
                <td className="ocultar-en-movil" style={styles.cell}>{piso.ruta}</td>
                <td className="ocultar-en-movil" style={styles.cell}>{piso.franja_horaria}</td>
                <td className="ocultar-en-movil" style={styles.cell}>{piso.supermercado}</td>
                <td style={{ ...styles.cell, minWidth: 172 }} onClick={e => e.stopPropagation()}>
                  <div style={styles.accionesFila}>
                    <button style={styles.historialFila} title="Historial" onClick={() => setDrawer({ filaId: piso.id, titulo: piso.abreviatura })}>🕓 Hist.</button>
                    <button style={styles.edit} title="Editar" onClick={() => navigate(`/pisos/editar/${piso.id}`)}>✏️ Ed.</button>
                    {baja
                      ? <span style={styles.yaDeBaja} title="El piso ya está de baja">De baja</span>
                      : <button style={styles.delete} title="Dar de baja" onClick={() => handleEliminar(piso.id)}>🗑 Baja</button>}
                  </div>
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
      )}

      <button style={styles.add} onClick={() => navigate("/pisos/nuevo")}>
        ➕ Añadir Piso
      </button>

      {pisoModal && <ModalPiso piso={pisoModal} onClose={() => setPisoModal(null)} onEdit={id => navigate(`/pisos/editar/${id}`)} />}

      <HistorialFilaDrawer
        abierto={!!drawer}
        endpoint="/api/pisos/historial"
        filaId={drawer?.filaId}
        titulo={drawer?.titulo}
        onCerrar={() => setDrawer(null)}
      />
    </div>
  );
};

const styles = {

  clearFilters: {
    padding: "12px 18px",
    backgroundColor: "#95a5a6",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "15px",
    whiteSpace: "nowrap",
  },
  container: {
    padding: "30px",
    fontFamily: "Segoe UI, sans-serif",
    position: "relative",
  },
  title: {
    fontSize: "26px",
    marginBottom: "20px",
    fontWeight: "700",
    textAlign: "center",
    color: "#2c3e50",
  },
  hdrFlex: { display: "flex", alignItems: "center", justifyContent: "center", gap: 4 },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    boxShadow: "0 2px 8px rgb(0 0 0 / 0.1)",
    borderRadius: "10px",
    overflow: "hidden",
  },
  headerCell: {
    padding: "16px 12px",
    backgroundColor: "#3498db",
    color: "white",
    fontWeight: "700",
    fontSize: "15px",
    textAlign: "center",
  },
  rowBaja: {
    background: "repeating-linear-gradient(135deg, #f1f5f9 0 12px, #e8edf3 12px 24px)",
    color: "#64748b",
  },
  cellBajaPrimera: { borderLeft: "5px solid #94a3b8" },
  badgeBaja: {
    display: "inline-block", background: "#475569", color: "#fff", fontWeight: 800, fontSize: 11.5,
    letterSpacing: 0.6, padding: "2px 9px", borderRadius: 999, whiteSpace: "nowrap",
  },
  yaDeBaja: {
    display: "inline-flex", alignItems: "center", padding: "6px 10px", borderRadius: 8, fontSize: 12.5,
    fontWeight: 700, color: "#64748b", background: "#e2e8f0", border: "1px dashed #94a3b8", whiteSpace: "nowrap",
  },
  row: {
    backgroundColor: "#fafafa",
    borderBottom: "1px solid #e1e1e1",
    transition: "background-color 0.3s ease",
  },
  cell: {
    padding: "12px",
    textAlign: "center",
    fontSize: "14px",
    color: "#34495e",
  },
  contadorFiltro: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    margin: "10px 0 4px",
    padding: "6px 14px",
    borderRadius: "999px",
    backgroundColor: "#eaf2fb",
    color: "#2c5c8f",
    fontSize: "13.5px",
    fontWeight: "500",
  },
  estadoGroup: {
    display: "flex",
    gap: "4px",
    backgroundColor: "#eef1f4",
    borderRadius: "8px",
    padding: "4px",
  },
  estadoBtn: {
    padding: "8px 14px",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "transparent",
    color: "#5a6472",
    fontWeight: "700",
    fontSize: "13.5px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  estadoBtnActivo: {
    padding: "8px 14px",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#3498db",
    color: "#fff",
    fontWeight: "700",
    fontSize: "13.5px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  accionesFila: {
    display: "flex",
    gap: "6px",
    justifyContent: "center",
  },
  edit: {
    padding: "6px 7px",
    backgroundColor: "#f39c12",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    color: "#fff",
    fontWeight: "600",
    fontSize: "11.5px",
    lineHeight: 1,
    whiteSpace: "nowrap",
    transition: "background-color 0.2s ease",
  },
  historialFila: {
    padding: "6px 7px",
    backgroundColor: "#5e35b1",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    color: "#fff",
    fontWeight: "600",
    fontSize: "11.5px",
    lineHeight: 1,
    whiteSpace: "nowrap",
    transition: "background-color 0.2s ease",
  },
  delete: {
    padding: "6px 7px",
    backgroundColor: "#e74c3c",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    color: "#fff",
    fontWeight: "600",
    fontSize: "11.5px",
    lineHeight: 1,
    whiteSpace: "nowrap",
    transition: "background-color 0.2s ease",
  },
  historial: {
    padding: "14px 26px",
    background: "linear-gradient(135deg, #7e57c2, #5e35b1)",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "16px",
    whiteSpace: "nowrap",
    boxShadow: "0 4px 10px rgba(94, 53, 177, 0.35)",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
  },
  import: {
    padding: "14px 26px",
    backgroundColor: "#27ae60",  // verde vibrante
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "16px",
    marginLeft: "12px",
    whiteSpace: "nowrap",
    boxShadow: "0 4px 8px rgba(39, 174, 96, 0.4)",  // un poco de sombra para resaltar
    transition: "background-color 0.3s ease",
  },
  add: {
    position: "fixed",
    bottom: "30px",
    right: "30px",
    padding: "16px 24px",
    fontSize: "18px",
    backgroundColor: "#27ae60",
    color: "#fff",
    border: "none",
    borderRadius: "50px",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgb(39 174 96 / 0.5)",
    transition: "background-color 0.3s ease",
  },
};

const DetalleItem = ({ label, value, valueStyle }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div style={{ fontSize: 13, color: "#34495e", padding: "4px 0" }}>
      <span style={{ fontWeight: 600, color: "#555", marginRight: 4 }}>{label}:</span>
      {valueStyle ? <span style={valueStyle}>{String(value)}</span> : String(value)}
    </div>
  );
};

// fecha_baja llega como fecha "naive" de Madrid serializada en GMT por Flask:
// se leen las partes UTC para no desplazarla de día.
const fechaBajaTexto = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return String(v).slice(0, 10);
  const p2 = (n) => String(n).padStart(2, "0");
  return `${p2(d.getUTCDate())}/${p2(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
};
const esBaja = (piso) => Number(piso.activo) === 0;

const edadDe = (iso) => {
  if (!iso) return null;
  const n = new Date(iso + "T00:00:00"), h = new Date();
  if (isNaN(n)) return null;
  let e = h.getFullYear() - n.getFullYear();
  if (h.getMonth() < n.getMonth() || (h.getMonth() === n.getMonth() && h.getDate() < n.getDate())) e--;
  return e;
};

const ModalPiso = ({ piso, onClose, onEdit }) => {
  const [alumnos, setAlumnos] = useState(null); // null = cargando
  const [errorAlumnos, setErrorAlumnos] = useState("");
  useEffect(() => {
    let cancelado = false;
    setAlumnos(null); setErrorAlumnos("");
    axios.get(`/api/pisos/${piso.id}/alumnos`)
      .then((res) => { if (!cancelado) setAlumnos(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelado) { setAlumnos([]); setErrorAlumnos("No se pudieron cargar los alumnos."); } });
    return () => { cancelado = true; };
  }, [piso.id]);
  const dias = [
    { label: "Lunes",     e: piso.l_e, s: piso.l_s },
    { label: "Martes",    e: piso.m_e, s: piso.m_s },
    { label: "Miércoles", e: piso.x_e, s: piso.x_s },
    { label: "Jueves",    e: piso.j_e, s: piso.j_s },
    { label: "Viernes",   e: piso.v_e, s: piso.v_s },
    { label: "Sábado",    e: piso.s_e, s: piso.s_s },
  ].filter(d => d.e || d.s);

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.box} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h3 style={{ margin: 0 }}>🏠 {piso.abreviatura} <span style={{ fontWeight: 400, fontSize: 14, color: "#888" }}>#{piso.id}</span>
            {esBaja(piso) && <span style={{ ...styles.badgeBaja, marginLeft: 10, fontSize: 12.5, verticalAlign: "middle" }}>🚫 DE BAJA{piso.fecha_baja ? ` · ${fechaBajaTexto(piso.fecha_baja)}` : ""}</span>}
          </h3>
          <button style={modalStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={modalStyles.grid}>
          <DetalleItem label="Empresa" value={piso.empresa} />
          <DetalleItem label="Sexo" value={piso.sexo} />
          <DetalleItem label="Servicio" value={piso.servicio} valueStyle={piso.servicio ? (() => { const sc = getServicioStyle(piso.servicio); return { background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, padding: "2px 10px", borderRadius: 8, fontWeight: 700, display: "inline-block" }; })() : undefined} />
          <DetalleItem label="Plazas ocupadas" value={piso.plazas_ocupadas} />
          <DetalleItem label="Horas/día" value={piso.horas_dia} />
          <DetalleItem label="Horas/semana" value={piso.horas_semana} />
          <DetalleItem label="ID asistenta" value={piso.id_asistenta} />
          <DetalleItem label="Nombre asistenta" value={piso.nombre_asistenta} />
          <DetalleItem label="Supermercado" value={piso.supermercado} />
          <DetalleItem label="Franja horaria" value={piso.franja_horaria} />
          <DetalleItem label="Supervisora" value={piso.user_supervisora} />
          <DetalleItem label="Comprador" value={piso.user_comprador} />
          <DetalleItem label="Ruta" value={piso.ruta} />
        </div>

        {dias.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={modalStyles.sectionTitle}>Horario semanal</div>
            <div style={modalStyles.horarioGrid}>
              {dias.map(d => (
                <div key={d.label} style={modalStyles.horarioItem}>
                  <span style={{ fontWeight: 700 }}>{d.label}</span>
                  <span>{d.e ? d.e.slice(0,5) : "--"} – {d.s ? d.s.slice(0,5) : "--"}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <div style={modalStyles.sectionTitle}>
            👥 Alumnos {alumnos && !errorAlumnos && <span style={{ fontWeight: 500, color: "#888" }}>({alumnos.length})</span>}
          </div>
          {alumnos === null ? (
            <div style={{ fontSize: 13, color: "#888" }}>Cargando alumnos…</div>
          ) : errorAlumnos ? (
            <div style={{ fontSize: 13, color: "#c0392b" }}>{errorAlumnos}</div>
          ) : alumnos.length === 0 ? (
            <div style={{ fontSize: 13, color: "#888" }}>No hay alumnos activos en este piso.</div>
          ) : (
            <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f1f5f9", color: "#334155" }}>
                    {["Hab.", "Nombre", "País", "Duración", "A/R", "Edad"].map((h) => (
                      <th key={h} style={{ padding: "7px 8px", textAlign: "left", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {alumnos.map((a, i) => (
                    <tr key={a.id_alumno} style={{ background: i % 2 ? "#fafafa" : "#fff", borderTop: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "6px 8px", fontWeight: 700 }}>{a.habitacion || "—"}</td>
                      <td style={{ padding: "6px 8px" }}>{a.nombre}</td>
                      <td style={{ padding: "6px 8px" }}>{a.pais || "—"}</td>
                      <td style={{ padding: "6px 8px" }}>{a.duracion || "—"}</td>
                      <td style={{ padding: "6px 8px" }}>{a.a_r || "—"}</td>
                      <td style={{ padding: "6px 8px" }}>{edadDe(a.fecha_nacimiento) ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            style={{ padding: "8px 20px", backgroundColor: "#f39c12", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
            onClick={() => { onClose(); onEdit(piso.id); }}
          >
            ✏️ Editar
          </button>
          <button
            style={{ padding: "8px 20px", backgroundColor: "#6c757d", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
            onClick={onClose}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

const modalStyles = {
  overlay: {
    position: "fixed", inset: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000,
  },
  box: {
    background: "#fff",
    borderRadius: 12,
    padding: "24px 28px",
    minWidth: 320,
    maxWidth: 640,
    width: "90%",
    maxHeight: "85vh",
    overflowY: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginBottom: 16, borderBottom: "1px solid #e5e7eb", paddingBottom: 12,
  },
  closeBtn: {
    background: "none", border: "none", fontSize: 18,
    cursor: "pointer", color: "#6b7280", lineHeight: 1,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "2px 24px",
  },
  sectionTitle: {
    fontWeight: 700, fontSize: 13, color: "#3498db",
    textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8,
  },
  horarioGrid: {
    display: "flex", flexWrap: "wrap", gap: "8px",
  },
  horarioItem: {
    display: "flex", flexDirection: "column", alignItems: "center",
    background: "#f0f7ff", border: "1px solid #d0e6f8",
    borderRadius: 8, padding: "6px 14px", fontSize: 13, color: "#2c3e50",
    minWidth: 80,
  },
};

export default AdminGestionPisos;
